import { IGenericModal, Modal } from 'components/Modal/Modal';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TaskTypeForm } from '../TaskTypeForm/TaskTypeForm';
import { ITaskType, ITaskTypePayload } from 'types/taskType';
import { deleteTaskType, editTaskType, getTaskType } from 'api/taskTypes';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'components/_form';
import { yupResolver } from '@hookform/resolvers/yup';
import { taskTypeSchema } from 'validation/taskTypeSchemas';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { IOption } from 'components/_form/NestedSelect/NestedSelect';
import { expandSelectedAreaIdsWithAncestors, findOptionByValueInTree } from 'components/_form/NestedSelect/utilities';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';

interface Props extends IGenericModal {
  onSubmitSuccess?: (newTaskType: ITaskType) => void;
  onDeleteSuccess?: () => void;
  editingTaskTypeId: number;
}

export const EditTaskTypeForm = ({
  isOpen,
  onCancelClick,
  onSubmitSuccess,
  onDeleteSuccess,
  editingTaskTypeId
}: Props) => {
  const [editingTaskType, setEditingTaskType] = useState<ITaskType>();

  const { t } = useTranslation();

  const methods = useForm<ITaskTypePayload['task_type']>({
    mode: 'onChange',
    resolver: yupResolver(taskTypeSchema)
  });

  const { data: areasData = [] } = useAreasQuery();
  const areaTreeRoots = useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );

  const { handleSubmit, reset } = methods;

  const handleSubmitTaskTypeForm = async (
    data: ITaskTypePayload['task_type']
  ) => {
    const areaIdsForApi = expandSelectedAreaIdsWithAncestors(
      areaTreeRoots,
      data.areas ?? []
    );

    const taskTypeRes = await editTaskType(editingTaskTypeId, {
      task_type: {
        ...data,
        area_ids: areaIdsForApi,
        approvers_attributes: [
          ...(data.approvers_attributes || []),
          ...(editingTaskType?.approvers
            ?.filter(
              (approvingStep) =>
                !data.approvers_attributes?.find(
                  (approvingStepsAttribute) =>
                    approvingStepsAttribute.id?.toString() ===
                    approvingStep?.id.toString()
                )
            )
            .map((approvingStep) => ({
              id: approvingStep.id,
              _destroy: 1
            })) || [])
        ]
      }
    });

    if ('error' in taskTypeRes) {
      toast.error(taskTypeRes.error.message);
      return;
    }

    onSubmitSuccess?.(taskTypeRes.data);
  };

  const handleDeleteTaskType = async () => {
    const deleteTaskTypeRes = await deleteTaskType(editingTaskTypeId);

    if (deleteTaskTypeRes && 'error' in deleteTaskTypeRes) {
      toast.error(deleteTaskTypeRes.error.message);
      return;
    }

    onDeleteSuccess?.();
  };

  const handleGetTaskType = useCallback(async () => {
    const taskTypeRes = await getTaskType(editingTaskTypeId);

    if ('error' in taskTypeRes) {
      return;
    }

    setEditingTaskType(taskTypeRes.data);
  }, [editingTaskTypeId]);

  const handleSetDefaultValues = useCallback(() => {
    reset({
      ...editingTaskType,
      areas:
        editingTaskType?.areas
          ?.map(({ id }) => findOptionByValueInTree(areaTreeRoots, String(id)))
          .filter((o): o is IOption => !!o) ?? [],
      execution_progress_tracking_id: editingTaskType?.execution_progress_tracking?.id,
      color_code: editingTaskType?.color_code || 'white',
      approvers_attributes:
        editingTaskType?.approvers.map(
          (functionApprovingStep) => ({
            ...functionApprovingStep,
            execution_progress_tracking_approval_steps_attributes:
              functionApprovingStep.execution_progress_tracking_approval_steps
          })
        )
    });
  }, [editingTaskType, reset, areaTreeRoots]);

  useEffect(() => {
    handleGetTaskType();
  }, [handleGetTaskType]);

  useEffect(() => {
    handleSetDefaultValues();
  }, [handleSetDefaultValues]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={() => {
        onCancelClick();
      }}
      headerTitle={t('administrationTasksView.editTaskType')}
      mainButton={{
        action: handleSubmit((data) => {
          handleSubmitTaskTypeForm(data);
        }),
        variant: 'eucalyptus',
        label: t('buttons.save')
      }}
      isSecondButtonVisible
      gridTemplateColumns="70%"
      additionalFooter={
        <Button variant="red" onClick={handleDeleteTaskType}>
          {t('buttons.delete')}
        </Button>
      }
    >
      <FormProvider {...methods}>
        <form>
          <TaskTypeForm />
        </form>
      </FormProvider>
    </Modal>
  );
};
